import { useReducer } from 'react';
import isPlainObject from 'lodash/isPlainObject';

export type FormFeedback = { readonly [field: string]: string };

type FeedbackRecord = { readonly [key: string]: unknown };

export type FormFeedbackAction =
    | { type: 'SET_SUCCESS'; payload: { readonly message: string } }
    | { type: 'SET_ERROR'; payload: unknown }
    | { type: 'CLEAR_ERRORS' };

const GLOBAL_ERROR_KEYS = ['team', 'promo', 'reason'] as const;

const hasOwnKey = (record: FeedbackRecord, key: string) =>
    Object.prototype.hasOwnProperty.call(record, key);

const isFeedbackRecord = (value: unknown): value is FeedbackRecord => isPlainObject(value);

const mergeFeedbackValue = (current: unknown, next: unknown): unknown => {
    if (Array.isArray(current) && Array.isArray(next)) {
        return next.length === 0 ? current : [...current, ...next];
    }
    if (isFeedbackRecord(current) && isFeedbackRecord(next)) {
        return mergeFeedbackRecords(current, next);
    }
    return next;
};

const mergeFeedbackRecords = <Feedback extends FeedbackRecord>(
    current: Feedback,
    next: FeedbackRecord,
): Feedback => {
    const changedEntries = Object.entries(next).flatMap(([key, nextValue]) => {
        if (!hasOwnKey(current, key)) return [[key, nextValue] as const];
        const mergedValue = mergeFeedbackValue(current[key], nextValue);
        return mergedValue === current[key] ? [] : [[key, mergedValue] as const];
    });
    return changedEntries.length === 0
        ? current
        : { ...current, ...Object.fromEntries(changedEntries) };
};

const setFeedbackField = <Feedback extends FeedbackRecord>(
    state: Feedback,
    field: string,
    value: unknown,
): Feedback =>
    hasOwnKey(state, field) && state[field] === value ? state : { ...state, [field]: value };

const formFeedbackReducer = (state: FormFeedback, action: FormFeedbackAction): FormFeedback => {
    switch (action.type) {
        case 'SET_SUCCESS':
            return setFeedbackField(state, 'success', action.payload.message);
        case 'SET_ERROR': {
            const { payload } = action;
            if (typeof payload === 'string') return setFeedbackField(state, 'global', payload);
            if (!isFeedbackRecord(payload)) return state;
            const globalErrorKey = GLOBAL_ERROR_KEYS.find((key) => hasOwnKey(payload, key));
            if (globalErrorKey) {
                return setFeedbackField(state, 'global', payload[globalErrorKey]);
            }
            return mergeFeedbackRecords(state, payload);
        }
        case 'CLEAR_ERRORS':
            return Object.fromEntries(Object.keys(state).map((field) => [field, '']));
        default:
            return state;
    }
};

export const useFormFeedback = (initialState: FormFeedback) =>
    useReducer(formFeedbackReducer, initialState);
